import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, User } from "lucide-react";

export default function PatientLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user.role === "patient") navigate("/patient");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === "patient") {
        toast.success("Welcome back!");
        navigate("/patient");
      } else if (data.role === "admin") {
        navigate("/admin");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="patient-login-page" className="min-h-[80vh] flex items-center justify-center bg-[#F9F9F8] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#00A4A9] flex items-center justify-center mx-auto mb-4">
            <User className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-outfit text-2xl font-medium text-[#03045e]" data-testid="patient-login-title">Patient Portal</h1>
          <p className="text-sm text-slate-500 mt-1">Sign in to track your treatment journey</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="patient-login-form">
          {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3 mb-5" data-testid="patient-login-error">{error}</div>}
          <div className="mb-5">
            <Label className="text-sm text-slate-700 mb-1.5 block">Email</Label>
            <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" placeholder="john@example.com" data-testid="patient-email-input" />
            </div>
          </div>
          <div className="mb-6">
            <Label className="text-sm text-slate-700 mb-1.5 block">Password</Label>
            <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" placeholder="Enter password" data-testid="patient-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white h-11" data-testid="patient-login-btn">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
          <p className="text-center text-sm text-slate-500 mt-4">
            Don't have an account? <Link to="/patient/register" className="text-[#00A4A9] hover:underline" data-testid="register-link">Create Account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
